// https://www.youtube.com/watch?v=12l6lkW6JhE
// https://www.youtube.com/watch?v=ZEKBDXGnD4s

import i18n, { t } from 'i18next'
import axios, { AxiosResponse } from 'axios'
import config, { getTokenHeader } from '../config'
import { accessToken as authAccessToken, refreshToken as authRefreshToken, authLogin, authLogout } from '@client/stores/authStore'
import error from '@client/stores/errorStore'
import tokenService from '@client/utils/tokenService'
import { useConnection } from '../components/ConnectionProvider'

// axios api

const api = axios.create({
    baseURL: config.serverUrl,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
})
//console.log(`create axios on port ${config.serverUrl} !!!`)

// token handling

api.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = i18n.language
    return config
})

// Calls that never renew the access token: renewing the renewal is the loop
// that asked /token sixty times in a row, and sign-in and sign-out carry no
// access token to renew.
const NO_RENEWAL = ['/token', '/login', '/logout']

// One renewal at a time: calls that fail together wait for the same one,
// instead of each asking /token for itself.
let renewal: Promise<void> | null = null

const renewAccessToken = () => {
    if (!renewal) {
        renewal = api
            .post('/token', { token: authRefreshToken.value })
            .then((rs) => {
                authAccessToken.value = rs.data.accessToken
                // a reload starts from the stored token, not the expired one
                tokenService.updateLocalAccessToken(rs.data.accessToken)
            })
            .finally(() => {
                renewal = null
            })
    }
    return renewal
}

let axiosErrorHandlingRegistered = false

export const initAxiosErrorHandling = () => {
    const { setIsServerReachable } = useConnection()

    if (axiosErrorHandlingRegistered) return

    api.interceptors.request.use(getTokenHeader, (error) => {
        return Promise.reject(error)
    })

    api.interceptors.response.use(
        (response) => {
            //setIsServerReachable(true)
            return response
        },
        async (error) => {
            const originalConfig = error.config
            let isServerReachable = true

            if (error.code === 'ECONNABORTED' || error.message.includes('Network Error')) {
                // Connection to server is interrupted
                isServerReachable = false
            }

            if (error.response && error.response.status >= 500) {
                // Server error
                isServerReachable = false
            }

            setIsServerReachable(isServerReachable)

            if (originalConfig && !NO_RENEWAL.includes(originalConfig.url) && error.response) {
                // access token has expired: renew it once and repeat the call
                // once. Signed out already, there is nothing to renew.
                if (error.response.status === 401 && !originalConfig._retry && authRefreshToken.value) {
                    originalConfig._retry = true

                    try {
                        await renewAccessToken()
                    } catch (err: any) {
                        // refused: the session is over. Signed out here, with
                        // the server's reason as the message (handleResponseError).
                        if (err?.response) err.signOut = true
                        return Promise.reject(err)
                    }
                    // the same call again; a second 401 is answered, not renewed
                    return api(originalConfig)
                }
            } else if (!isServerReachable) {
                const _check = () => {
                    setTimeout(() => {
                        if (!navigator.onLine) _check()
                        else setIsServerReachable(true)
                    }, 1000)
                }
                _check()
            }

            return Promise.reject(error)
        }
    )

    axiosErrorHandlingRegistered = true
}

// helper

// test if you need response data directly in the response header
// let buildInfo = {
//     date: undefined,
//     commit: undefined,
//     isDaily: undefined,
//     isDemo: undefined,
// }
// api.interceptors.response.use((response) => {
//     const info = response.headers['x-build-info'] || null;
//     if (info) buildInfo = JSON.parse(info)
//     return response;
// });
// export const getBuildInfop = () => buildInfo

export function parseQuery(text: string, obj: object) {
    let result: string[] = []
    if (obj) {
        result = Object.keys(obj).map((key: string) => {
            let value = encodeURI((obj as any)[key])
            return `${key}=` + value
        })
    }

    let strText = text
    if (result.length) strText += '&'
    strText += result.join('&')
    return strText
}

// response handling

export type TableListResponseType = { total: number; page: number; rowCount: number; rows: any[] }

function handleResponseError(err: any) {
    if (err.response) {
        // console.log('data:', err.response.data)
        // console.log('status:', err.response.status)
        // console.log('headers:', err.response.headers)
        // The message, not the envelope it came in. The server answers errors as
        // `{ message: '…' }`, and handing that object to t() produced the
        // "[object Object]" that used to stand under "Fehler 400" — with the one
        // sentence that says what went wrong sitting inside it.
        const data = err.response.data
        const msg =
            (typeof data === 'string' ? data : data?.error || data?.message) || ''
        // t() answers undefined for anything it has no translation for, and
        // server messages are english sentences rather than known keys.
        // signed out already: what was still on its way has nothing to say
        if (err.response.status === 401 && !authRefreshToken.value) return
        error.setError(t(msg) || msg || t('An error has occurred:'), err.response.status)
        // Only a refused renewal signs out: the session is over. Any other 403
        // means "not allowed" and stays a notice (operator, 14.10.2026).
        if (err.signOut) {
            authLogout()
        }
    } else {
        console.log('Error:', err.message)
        error.setError(t(err.message), 500)
    }

    //throw err
}

// interfaces

export type TAxiosResponse = Promise<AxiosResponse<any, any> | undefined>
export interface ITableListModel<T = any> {
    list({ mode, project, page, limit }: TableListRequestType): TAxiosResponse
    read(project: string, id: string | object): TAxiosResponse
    create(project: string, data: T): TAxiosResponse
    update(project: string, id: string | object, data: T): TAxiosResponse
    delete(project: string, id: string | object): TAxiosResponse
    count(project: string): TAxiosResponse
}

// request handling

export const TABLE_REQUEST_MODE = {
    PAGINATION: 'pagination',
    POSITION: 'position',
}
export type TableListRequestType = {
    mode: string
    project?: string
    dataList?: string
    page?: number
    limit?: number
    /** what the lists pass for the page size — the same as limit */
    count?: number
    query?: object
    q?: string // search text
}

export async function forgotPassword(userName: string) {
    const result = await api.post('/forgot-password', { userName })
    if (!result.data) {
        throw new Error(t('Failed to send password reset instructions.'))
    }
}
export async function resetPassword(code: string, password: string, confirmPassword: string) {
    const result = await api.post(`/reset-password/${code}`, { password, confirmPassword })
    if (!result.data) {
        throw new Error(t('Failed to send reset the new password.'))
    }
}

export async function login(userName: string, password: string) {
    let result
    try {
        result = await api.post('/login', { userName, password })
    } catch (err: any) {
        // A refusal (deactivated account, ended subscription) comes as 403 with
        // a sentence; without this the form showed "Request failed with status
        // code 403". The login form translates it.
        const message = err?.response?.data?.message
        if (typeof message === 'string' && message) throw new Error(message)
        throw err
    }
    if (typeof result.data === 'string') {
        throw new Error(t(result.data).toString())
    }

    const secret = result.data
    authLogin(secret)
    //console.log('db: login result', secret)
    return secret.user
}

export async function logout() {
    const token = authRefreshToken
    if (token) {
        await api.post('/logout', { token } as any)
        authLogout()
    }
    //console.log('db: logout')
    return true
}

export async function getData(url: string, data: any = null, returnError: boolean = false): Promise<any> {
    try {
        return await api.get(url, data)
    } catch (err: any) {
        if (returnError) return err.response
        handleResponseError(err)
    }
}

export async function postData(url: string, data: any, options = {}) {
    try {
        return await api.post(url, data, options)
    } catch (err) {
        handleResponseError(err)
    }
}

export async function patchData(url: string, data: any, options = {}) {
    try {
        return await api.patch(url, data, options)
    } catch (err) {
        handleResponseError(err)
    }
}

export async function deleteData(url: string, data: any = null) {
    try {
        return await api.delete(url, data)
    } catch (err) {
        handleResponseError(err)
    }
}

export async function countData(url: string, data: any = null) {
    try {
        return await api.get(url, data)
    } catch (err) {
        handleResponseError(err)
    }
}
